/**
 * Multi-series daily chart for the Data Explorer + batch Data tab.
 *
 * Built on `@mantine/charts` (recharts). Volume and energy series land on
 * separate y-axes (`unitGroup`); missing days render as gaps
 * (`connectNulls={false}`); the tooltip shows the EXACT decimal string from
 * the wire, not the float used for pixel geometry. Period totals from the calc
 * engine can be overlaid as horizontal reference lines.
 */
import { BarChart, LineChart } from "@mantine/charts";
import { Box, Center, Group, Paper, Stack, Text } from "@mantine/core";
import type { TooltipProps } from "recharts";
import { MAX_RANGE_DAYS, enumerateDays } from "../../lib/dateRange";
import type { ChartSeriesInput } from "./series-data";
import { formatBucketLabel, formatCell, toPlotNumber } from "./series-data";
import { plotScale, unitGroup, unitLabel } from "./units";

export type ChartReferenceLine = {
  label: string;
  value: string;
  unit: string;
  color?: string;
};

type Props = {
  series: ChartSeriesInput[];
  referenceLines?: ChartReferenceLine[];
  variant?: "line" | "bar";
  height?: number;
  /**
   * The requested gas-day window. When given, the x-axis spans the whole range
   * so missing days at the boundaries (not just interior gaps) render as blanks.
   * Omit for inputs with no requested range (e.g. the upload-rows drill-in).
   */
  range?: { start: string; end: string };
  /** "daily" (gas-day x-axis) or "hourly" (bucket-start x-axis). */
  granularity?: "daily" | "hourly";
};

type SeriesMeta = { label: string; unit: string; color: string };

function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function SeriesChart({
  series,
  referenceLines = [],
  variant = "line",
  height = 340,
  range,
  granularity = "daily",
}: Props) {
  const visible = series.filter((s) => s.points.length > 0);

  if (visible.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Center mih={height - 80}>
          <Text c="dimmed" size="sm">
            Select one or more sources to plot a daily series.
          </Text>
        </Center>
      </Paper>
    );
  }

  // Recharts treats a dotted dataKey as a nested object path, but our series
  // ids legitimately contain dots (e.g. a dairy-scoped
  // `dairy_outlet_volume::com.aemetis.ackerman_dairy`). Plotting under the raw
  // id makes recharts resolve `name` as a path and read `undefined`, so the
  // series silently vanishes. Give each series a dot-free internal key for all
  // chart geometry; the original id stays on the input for cache/selection only.
  const keyed = visible.map((s, i) => ({ ...s, key: `s${i}` }));

  // Axis assignment. The chart has exactly two y-axes, so it can faithfully
  // carry at most two unit groups (each axis owns one group's scale + label).
  // Take the first two distinct groups in series order; a series in any further
  // group has no correctly-scaled axis, so we exclude it from the plot (and note
  // it below) rather than drawing it against an unrelated axis.
  const groupsInOrder: string[] = [];
  for (const s of keyed) {
    const g = unitGroup(s.unit);
    if (!groupsInOrder.includes(g)) groupsInOrder.push(g);
  }
  const leftGroup = groupsInOrder[0];
  const rightGroup = groupsInOrder[1];
  const axisFor = (unit: string): "left" | "right" | null => {
    const g = unitGroup(unit);
    if (g === leftGroup) return "left";
    if (rightGroup !== undefined && g === rightGroup) return "right";
    return null;
  };

  const renderable = keyed.filter((s) => axisFor(s.unit) !== null);
  const excluded = keyed.filter((s) => axisFor(s.unit) === null);
  const usesRight = renderable.some((s) => axisFor(s.unit) === "right");

  const leftUnit = renderable.find((s) => axisFor(s.unit) === "left")?.unit;
  const rightUnit = renderable.find((s) => axisFor(s.unit) === "right")?.unit;

  // Build the merged day-keyed rows recharts consumes (one row per day, a
  // numeric `[key]` + an exact-string `[key]__s` per renderable series).
  const dayset = new Set<string>();
  for (const s of renderable) for (const p of s.points) dayset.add(p.day);
  const present = [...dayset].sort();
  // Span the full requested range when known, so missing days at the window
  // boundaries also render as blanks; otherwise fall back to the present
  // min→max. Either way the gaps become explicit null rows, so
  // `connectNulls={false}` breaks the line at an outage instead of drawing
  // straight across it.
  // Hourly buckets aren't gas-days — plot the actual bucket keys (already sorted
  // time order); daily fills the requested gas-day range so boundary gaps show.
  const days =
    granularity === "hourly"
      ? present
      : range
        ? enumerateDays(range.start, range.end)
        : present.length > 0
          ? enumerateDays(present[0], present[present.length - 1])
          : present;

  // enumerateDays caps a daily range at MAX_RANGE_DAYS; if the last plotted day
  // falls short of the requested end, the tail was truncated — surface it rather
  // than silently dropping days off the right edge of the axis (BIO-144 #6).
  const truncated =
    granularity !== "hourly" && !!range && days.length > 0 && days[days.length - 1] < range.end;

  // Scale each series' plotted float into its axis's reference unit, so units
  // that share an axis but differ only by magnitude (SCF vs MSCF) align instead
  // of the smaller flattening to the baseline. Geometry only — the tooltip/CSV
  // still use the exact decimal string. The axis label stays the reference
  // unit, so the basis (HHV/LHV) on the energy axis is preserved.
  const scaleFor = (unit: string): number =>
    plotScale(unit) / plotScale(axisFor(unit) === "left" ? leftUnit : rightUnit);

  const byDay = new Map<string, Record<string, string | number | undefined>>();
  for (const day of days) byDay.set(day, { day });
  for (const s of renderable) {
    const scale = scaleFor(s.unit);
    for (const p of s.points) {
      const n = toPlotNumber(p.value);
      if (n === null) continue;
      const row = byDay.get(p.day);
      if (!row) continue;
      row[s.key] = n * scale;
      row[`${s.key}__s`] = p.value ?? "";
    }
  }
  const data = days.map((d) => byDay.get(d) ?? { day: d });

  const meta: Record<string, SeriesMeta> = {};
  for (const s of renderable) meta[s.key] = { label: s.label, unit: s.unit, color: s.color };

  const chartSeries = renderable.map((s) => ({
    name: s.key,
    label: s.label,
    color: s.color,
    yAxisId: axisFor(s.unit) ?? "left",
  }));

  const refLines = referenceLines
    .map((r) => {
      const y = toPlotNumber(r.value);
      const axis = axisFor(r.unit);
      if (y === null || axis === null) return null;
      return {
        y: y * scaleFor(r.unit),
        label: r.label,
        color: r.color ?? "gray.6",
        yAxisId: axis,
        labelPosition: "insideTopRight" as const,
        strokeDasharray: "4 4",
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  const common = {
    data,
    dataKey: "day",
    series: chartSeries,
    h: height,
    valueFormatter: compact,
    gridAxis: "xy" as const,
    withLegend: false,
    tickLine: "xy" as const,
    yAxisLabel: leftUnit ? unitLabel(leftUnit) : undefined,
    withRightYAxis: usesRight,
    rightYAxisLabel: rightUnit ? unitLabel(rightUnit) : undefined,
    referenceLines: refLines,
    xAxisProps: {
      minTickGap: 24,
      tickFormatter: (v: string) => formatBucketLabel(String(v), granularity),
    },
    tooltipProps: {
      content: (props: TooltipProps<number, string>) => (
        <ChartTooltip
          day={
            props.label != null ? formatBucketLabel(String(props.label), granularity) : undefined
          }
          payload={props.payload}
          meta={meta}
        />
      ),
    },
  };

  return (
    <Stack gap="sm">
      {variant === "bar" ? (
        <BarChart {...common} />
      ) : (
        <LineChart
          {...common}
          connectNulls={false}
          curveType="linear"
          strokeWidth={2}
          withDots={false}
        />
      )}
      <ChartLegend series={renderable} />
      {excluded.length > 0 ? (
        <Text size="xs" c="dimmed">
          Not plotted — the chart shows two unit axes at a time:{" "}
          {excluded.map((s) => `${s.label} (${unitLabel(s.unit)})`).join(", ")}.
        </Text>
      ) : null}
      {truncated ? (
        <Text size="xs" c="orange.7">
          Showing the first {MAX_RANGE_DAYS} days — the selected range exceeds the {MAX_RANGE_DAYS}
          -day maximum. Narrow the window to see the rest.
        </Text>
      ) : null}
    </Stack>
  );
}

function ChartTooltip({
  day,
  payload,
  meta,
}: {
  day?: string;
  payload?: TooltipProps<number, string>["payload"];
  meta: Record<string, SeriesMeta>;
}) {
  if (!payload || payload.length === 0) return null;
  return (
    <Paper px="md" py="sm" withBorder shadow="md" radius="md">
      <Text size="xs" fw={600} mb={6}>
        {day}
      </Text>
      <Stack gap={4}>
        {payload.map((entry) => {
          const name = String(entry.name ?? "");
          const m = meta[name];
          // The wire decimal string (never the plotted float), shown rounded to
          // 2 dp for display; the CSV export keeps the exact value.
          const row = entry.payload as Record<string, unknown> | undefined;
          const exact = row?.[`${name}__s`];
          return (
            <Group key={name} gap={8} wrap="nowrap" justify="space-between">
              <Group gap={6} wrap="nowrap">
                <Box w={8} h={8} style={{ borderRadius: 999, backgroundColor: entry.color }} />
                <Text size="xs">{m?.label ?? name}</Text>
              </Group>
              <Text size="xs" ff="monospace" fw={500}>
                {exact != null ? formatCell(String(exact), 2) : "—"}{" "}
                <Text span size="xs" c="dimmed">
                  {m ? unitLabel(m.unit) : ""}
                </Text>
              </Text>
            </Group>
          );
        })}
      </Stack>
    </Paper>
  );
}

function ChartLegend({ series }: { series: ChartSeriesInput[] }) {
  return (
    <Group gap="lg" wrap="wrap">
      {series.map((s) => (
        <Group key={s.id} gap={6} wrap="nowrap">
          <Box w={10} h={10} style={{ borderRadius: 2, backgroundColor: colorVar(s.color) }} />
          <Text size="xs">{s.label}</Text>
          <Text size="xs" c="dimmed">
            {unitLabel(s.unit)}
          </Text>
        </Group>
      ))}
    </Group>
  );
}

/** Resolve a Mantine "name.shade" color reference to its CSS variable. */
function colorVar(ref: string): string {
  const [name, shade = "6"] = ref.split(".");
  return `var(--mantine-color-${name}-${shade})`;
}
